import {
  type GetOrganisationFeaturesError,
  type GetOrganisationIdpConnectionsError,
  useGetOrganisationFeatures,
  useGetOrganisationIdpConnections,
  usePatchOrganisationFeatures,
  useUpdateOrganisationIdpConnection,
} from '@/generated/http-clients/v3ApiComponents'
import type {
  IdpConnection,
  IdpConnectionsResponse,
  OrganisationFeatures,
} from '@/generated/http-clients/v3ApiSchemas'
import { DEFAULT_REFRESH_INTERVAL_IN_MILLISECONDS } from '@/helpers/constants'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import {
  ERROR_TOAST_OPTION,
  LOADING_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
  toaster,
} from '@/hooks/useToast/toastUtils'
import type { UseQueryResult } from '@tanstack/react-query'
import get from 'lodash/get'
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'

type OrganizationContextType = {
  /**
   * Currently selected organization id
   */
  selectedOrganizationId: string
  /**
   * Set the selected organization id
   */
  setSelectedOrganizationId: (organizationId: string) => void
  /**
   * Clear the selected organization id
   */
  clearSelectedOrganizationId: () => void
  orgFeatures: UseQueryResult<
    OrganisationFeatures,
    GetOrganisationFeaturesError
  >
  /**
   * Patch the organization features
   */
  patchOrganisationFeatures: (
    orgFeatures: OrganisationFeatures,
  ) => Promise<OrganisationFeatures>
  idp: {
    /**
     * Collection of IdP connections for the selected organization
     */
    idpConnections: UseQueryResult<
      IdpConnectionsResponse,
      GetOrganisationIdpConnectionsError
    >
    /**
     * Update the IdP connection for the selected organization
     *
     * @param connectionId The IdP connection id
     * @param enabled Whether the connection should be enabled or disabled
     */
    updateOrgIdpConnection: (
      connectionId: string,
      enabled: boolean,
    ) => Promise<IdpConnection>
  }
}

const OrganizationContext = createContext<OrganizationContextType>(
  {} as OrganizationContextType,
)

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider',
    )
  }
  return context
}

const ACTIVE_ORG_ID_KEY = 'hivemq-cloud-backoffice:activeOrganizationId'

/**
 * Provider to manage the selected organization
 *
 * Needs to be installed globally to provide the selected organization to all components and implicit requests
 *
 * @dependency OrganizationsProvider
 */
export function OrganizationProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const { t } = useTranslation('features')

  const [localData, setLocalData] = useLocalStorage(ACTIVE_ORG_ID_KEY, {
    selectedOrganizationId: '',
  })

  // Helper function to determine if we have a valid organization ID to use for API calls
  const hasValidOrgId = Boolean(localData.selectedOrganizationId)

  // Only fetch organization features when an organization is selected and it's not empty
  const orgFeatures = useGetOrganisationFeatures(
    {
      pathParams: {
        orgId: localData.selectedOrganizationId,
      },
    },
    {
      // Skip the query if no organization is selected
      enabled: hasValidOrgId,
      // Don't retry failed requests when orgId is missing or invalid
      retry: hasValidOrgId,
    },
  )

  const patchOrganisationFeaturesRequest = usePatchOrganisationFeatures({
    onMutate: () => {
      const toastId = 'org-features-patch-toast'
      toaster.create({
        ...LOADING_TOAST_OPTION,
        id: toastId,
        title: t('organization.features.patchOrgFeatures.loadingTitle'),
        description: t(
          'organization.features.patchOrgFeatures.loadingDescription',
        ),
      })
      return toastId // Return the toastId for subsequent stages
    },
    onSuccess: (_data: unknown, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...SUCCESS_TOAST_OPTION,
        title: t('organization.features.patchOrgFeatures.successTitle'),
        description: t(
          'organization.features.patchOrgFeatures.successDescription',
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
      orgFeatures.refetch()
    },
    onError: (error, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...ERROR_TOAST_OPTION,
        title: t('organization.features.patchOrgFeatures.errorTitle'),
        description: String(
          get(error, 'stack.description') ||
            get(error, 'stack._embedded.errors[0].message') ||
            get(error, 'message') ||
            t('organization.features.patchOrgFeatures.errorDescription'),
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
  })

  const patchOrganisationFeatures = useCallback(
    (orgFeatures: OrganisationFeatures) => {
      // Don't attempt to patch if we don't have a valid organization ID
      if (!hasValidOrgId) {
        return Promise.reject(new Error('No valid organization ID selected'))
      }

      return patchOrganisationFeaturesRequest.mutateAsync({
        pathParams: {
          orgId: localData.selectedOrganizationId,
        },
        body: orgFeatures,
      })
    },
    [
      hasValidOrgId,
      localData.selectedOrganizationId,
      patchOrganisationFeaturesRequest,
    ],
  )

  const updateOrgIdpConnectionRequest = useUpdateOrganisationIdpConnection({
    onMutate: () => {
      const toastId = 'org-features-update-idp-connection-toast'
      toaster.create({
        ...LOADING_TOAST_OPTION,
        id: toastId,
        title: t('organization.features.updateOrgIdpConnection.loadingTitle'),
        description: t(
          'organization.features.updateOrgIdpConnection.loadingDescription',
        ),
      })
      return toastId // Return the toastId for subsequent stages
    },
    onSuccess: (_data: unknown, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...SUCCESS_TOAST_OPTION,
        title: t('organization.features.updateOrgIdpConnection.successTitle'),
        description: t(
          'organization.features.updateOrgIdpConnection.successDescription',
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
      idpConnections.refetch()
    },
    onError: (error, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...ERROR_TOAST_OPTION,
        title: t('organization.features.updateOrgIdpConnection.errorTitle'),
        description: String(
          get(error, 'stack.description') ||
            get(error, 'stack._embedded.errors[0].message') ||
            get(error, 'message') ||
            t('organization.features.updateOrgIdpConnection.errorDescription'),
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
  })

  const idpConnections = useGetOrganisationIdpConnections(
    {
      pathParams: {
        orgId: localData.selectedOrganizationId,
      },
    },
    {
      refetchInterval: DEFAULT_REFRESH_INTERVAL_IN_MILLISECONDS,
      // Skip the query if no organization is selected
      enabled: hasValidOrgId,
      // Don't retry failed requests when orgId is missing or invalid
      retry: hasValidOrgId,
    },
  )

  const updateOrgIdpConnection = useCallback(
    (connectionId: string, enabled: boolean) => {
      // Don't attempt to update if we don't have a valid organization ID
      if (!hasValidOrgId) {
        return Promise.reject(new Error('No valid organization ID selected'))
      }

      return updateOrgIdpConnectionRequest.mutateAsync({
        pathParams: {
          orgId: localData.selectedOrganizationId,
          connectionId,
        },
        body: {
          enabled,
        },
      })
    },
    [
      hasValidOrgId,
      localData.selectedOrganizationId,
      updateOrgIdpConnectionRequest,
    ],
  )

  const setSelectedOrganizationId = useCallback(
    (newId: string) => {
      setLocalData({ selectedOrganizationId: newId })
    },
    [setLocalData],
  )

  const clearSelectedOrganizationId = useCallback(() => {
    setLocalData({ selectedOrganizationId: '' })
  }, [setLocalData])

  const idpContext = useMemo(
    () => ({
      idpConnections,
      updateOrgIdpConnection,
    }),
    [idpConnections, updateOrgIdpConnection],
  )

  const contextValue = useMemo(
    () => ({
      selectedOrganizationId: localData.selectedOrganizationId,
      setSelectedOrganizationId,
      clearSelectedOrganizationId,
      orgFeatures,
      patchOrganisationFeatures,
      idp: idpContext,
    }),
    [
      localData.selectedOrganizationId,
      setSelectedOrganizationId,
      clearSelectedOrganizationId,
      orgFeatures,
      patchOrganisationFeatures,
      idpContext,
    ],
  )

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  )
}
